import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { useAuth } from '../store/auth.ts'
import { useChats } from '../store/chats.ts'
import type { Family } from '../api/types.ts'

/**
 * Новая переписка.
 *
 * Один выбранный — это разговор один на один, и название ему не нужно:
 * им служит имя собеседника. Несколько — группа, и вот ей название
 * уже необходимо, иначе в списке будет не отличить одну от другой.
 */
export function NewChatDialog({ onClose }: { onClose: () => void }) {
  const me = useAuth((s) => s.user)
  const createGroup = useChats((s) => s.createGroup)
  const navigate = useNavigate()

  const [family, setFamily] = useState<Family | null>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api<{ family: Family }>('/api/family')
      .then((data) => setFamily(data.family))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }, [])

  const others = family?.members.filter((m) => m.id !== me?.id) ?? []
  const isGroup = picked.length > 1

  const toggle = (id: string) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    )

  const create = async () => {
    if (picked.length === 0) return
    setBusy(true)
    setError(null)
    try {
      const chatId = await createGroup(isGroup ? title.trim() || 'Группа' : '', picked)
      onClose()
      navigate(`/chat/${chatId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось создать')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>Новая переписка</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {!family && !error && (
          <div className="center-screen" style={{ height: 100 }}>
            <div className="spinner" />
          </div>
        )}

        {family && others.length === 0 && (
          <p className="sheet-text">
            В семье пока только ты. Пригласи кого-нибудь — и здесь появится,
            с кем переписываться.
          </p>
        )}

        {others.length > 0 && (
          <>
            <div className="member-list">
              {others.map((member) => (
                <button
                  key={member.id}
                  className={'member-row pick' + (picked.includes(member.id) ? ' is-picked' : '')}
                  onClick={() => toggle(member.id)}
                >
                  <Avatar
                    id={member.id}
                    name={member.displayName}
                    url={member.avatarUrl}
                    size={40}
                    online={member.online}
                  />
                  <div className="member-text">
                    <div className="member-name">{member.displayName}</div>
                  </div>
                  <span className="pick-mark">{picked.includes(member.id) ? '✓' : ''}</span>
                </button>
              ))}
            </div>

            {isGroup && (
              <label className="field">
                <span className="field-label">Название группы</span>
                <input
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Родители, Дача, Отпуск…"
                  maxLength={64}
                />
              </label>
            )}

            {error && <div className="sheet-error">{error}</div>}

            <button
              className="btn-primary"
              onClick={() => void create()}
              disabled={busy || picked.length === 0}
            >
              {busy
                ? '…'
                : picked.length === 0
                  ? 'Выбери, с кем'
                  : isGroup
                    ? `Создать группу (${picked.length})`
                    : 'Написать'}
            </button>
          </>
        )}

        {!family && error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
